<script lang="ts">

    import {appEntrypoint, userContainer, toaster, productMappings} from "$lib/stores/stores.svelte"
    import {flip} from 'svelte/animate';
    import {fade} from 'svelte/transition';


  import { onDestroy } from 'svelte';
  import { searchProducts, type PageInfo } from '$lib/utils/searchProducts';
  import {
    PRODUCT_TYPE, getResponsiveImageSrcSet, getShopifyImageUrl, formatTitle, formatPrice, slugify,
     type ProductNode,
	 getFirstVariant} from '$lib/utils/shopify';
  import { localizeHref } from "$lib/paraglide/runtime";
  
   import type { Action } from "svelte/action";

    onDestroy   (() => {
        userContainer.searchText = "";
    });

    /* ───────── state ───────── */
  let searchText = $derived(userContainer.searchText);

  interface ResultState {
    items: ProductNode[];
    after: PageInfo["endCursor"];
    hasNext: boolean;
    loading: boolean;
    error: string | null;
  }

  let resultState = $state<ResultState>({
    items: [], after: null, hasNext: false, loading: false, error: null
  });

/* ───────── helpers ───────── */
  async function runSearch(reset = true) {
    if (!searchText.trim()) {
      resultState = { items: [], after: null, hasNext: false, loading: false, error: null };
      return;
    }
    try {
      resultState = { ...resultState, loading: true };
      const { products, pageInfo } = await searchProducts({
        search: searchText,
        first : 10,
        after : reset ? null : resultState.after,
        productType: PRODUCT_TYPE,
      });
      resultState = {
        items   : reset ? products : [...resultState.items, ...products],
        after   : pageInfo.endCursor,
        hasNext : pageInfo.hasNextPage,
        loading : false,
        error   : null
      };
    } catch (err) {
      resultState = { ...resultState, loading:false, error:(err as Error).message };
    }
  }

let searching = $derived(resultState.loading);
// Filter out products that don't have a prerendered page (no slug mapping = no page exists)
// If mappings haven't loaded yet (empty), show all results as fallback
let hasMappings = $derived(Object.keys(productMappings.slugMapping).length > 0);
let queryResults = $derived(
  hasMappings
    ? resultState.items.filter(item => productMappings.slugMapping[item.handle])
    : resultState.items
);
let prevSearch = $state("")
$effect(() => {
  if (searchText !== prevSearch) {
    prevSearch = searchText;
    console.log("searchText: "+$state.snapshot(searchText))
    runSearch(true);
  }
})

function loadMore() {
    if (resultState.hasNext && !resultState.loading) runSearch(false);
  }

  onDestroy(() => { userContainer.searchText = ''; });




let initSearch: Action = (e)=>{
  e.focus()
}
</script>
<!-- 1. Search Overlay Backdrop -->
<div
  class="fixed inset-0  backdrop-blur-sm z-50 overflow-y-auto"
>
  <div class="max-w-5xl mx-auto px-4 pt-8 pb-16">

    <!-- 2. Search Bar + Cancel -->
    <div class="relative">
      <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
        <!-- Your search icon here -->
        <svg class="h-5 w-5 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
      </div>

      <input
        type="text"
        placeholder="Search.."
        bind:value={userContainer.searchText}
        class="w-full border-b border-gray-300 bg-transparent py-3 ps-10 pe-12 placeholder-gray-500 focus:outline-none focus:border-black"
        style="view-transition-name: search-input;"
        use:initSearch
      />

      <button
        onclick={()=>{
            userContainer.searchText = ""
            // userContainer.showingSearch = false
        }}
        class="absolute inset-y-0 end-0 flex items-center pe-3 text-sm font-medium text-gray-700"
      >
        Cancel
      </button>
    </div>

    <!-- 3. Top Suggestions -->
    <!-- <div class="mt-8">
      <h2 class="text-sm font-semibold text-gray-500">Top Suggestions</h2>
      <ul class="mt-2 space-y-2">
        <li class="text-lg font-semibold cursor-pointer hover:underline">air force 1</li>
        <li class="text-lg cursor-pointer hover:underline">air max</li>
        <li class="text-lg cursor-pointer hover:underline">air max 95</li>
        <li class="text-lg cursor-pointer hover:underline">air max 90</li>
      </ul>
    </div> -->

    <!-- 4. Product Results -->
    <div class="mt-10">
      <ul
        class="
          flex
          space-x-6
          overflow-x-auto
          snap-x snap-mandatory
          md:grid md:grid-cols-3 md:gap-x-6 md:overflow-visible md:snap-none
          lg:flex lg:flex-row
        "
      >
        <!-- repeat this <li> for each product -->
            {#each queryResults as result (result.id)}
            {@const firstImage = result.images.edges[0]?.node}
            {@const firstVariant = getFirstVariant(result)}
            {@const isSoldOut = result.variants.edges.every(v => v.node.availableForSale === false)}
            {@const resultSlug = productMappings.slugMapping[result.handle] ?? slugify(result.title)}
            {@const resultTitle = productMappings.titleMapping[result.handle] ?? formatTitle(result.title)}

            <div
            transition:fade={{ duration: 200 }}
            >
            <a
            href={localizeHref(`/shoes/${resultSlug}/`)}
            >
        <li class="flex-shrink-0 w-64 snap-start md:snap-none">
            {#if firstImage}
                <img
                  src={getShopifyImageUrl(firstImage.url, 600, 'webp')}
                  alt={firstImage.altText ?? resultTitle}
                  class="w-full h-auto object-contain rounded"
                  style="view-transition-name: img-{result.handle}"
                />
                {/if}
          <h3 class="mt-4 text-base font-semibold">{resultTitle}</h3>
          <!-- <p class="text-sm text-gray-500">{result.tags}</p> -->
          <p class="mt-1 text-base font-bold">{formatPrice(firstVariant.price)}</p>
        </li>
    </a>
</div>
        {/each}
        <!-- …more products -->
      </ul>
    </div>
  </div>
</div>
